/**
 * Brand Config Context
 * 提供品牌配置的 React Context，支持在组件树任意位置获取配置
 */

import { createContext, useContext, type ReactNode } from 'react'

import type { CoreBrandConfig } from '../config/types'

/** Context 默认值为 null，使用时必须在 Provider 内 */
const BrandConfigContext = createContext<CoreBrandConfig | null>(null)

interface BrandConfigProviderProps {
  children: ReactNode
  /** 品牌配置（完整的 BrandConfig 会被接受，因为它扩展了 CoreBrandConfig） */
  config: CoreBrandConfig
}

/**
 * 品牌配置 Provider
 * 在应用根组件使用，注入完整的品牌配置
 */
export function BrandConfigProvider({ children, config }: BrandConfigProviderProps) {
  return <BrandConfigContext.Provider value={config}>{children}</BrandConfigContext.Provider>
}

/**
 * 获取完整的品牌配置
 * @throws 如果在 BrandConfigProvider 外部使用会抛出错误
 */
export function useBrandConfig(): CoreBrandConfig {
  const config = useContext(BrandConfigContext)
  if (!config) {
    throw new Error('useBrandConfig must be used within BrandConfigProvider')
  }
  return config
}

/**
 * 获取品牌 API 配置
 * 便捷 hook，只获取 api 部分配置
 */
export function useBrandAPI() {
  const config = useBrandConfig()
  return config.api
}

/**
 * 获取品牌基本信息
 * 便捷 hook，获取 id, name
 */
export function useBrandInfo() {
  const config = useBrandConfig()
  return {
    id: config.id,
    name: config.name,
  }
}
